// saveQueueService.js - Background save queue that persists even after component unmount
class SaveQueueService {
  constructor() {
    this.queue = [];
    this.processing = false;
    this.retryMap = new Map(); // Track retry attempts
    this.maxRetries = 3;
    this.listeners = new Set();
  }

  // Add a save operation to the queue
  enqueue(taskId, field, value, saveFunction) {
    // Remove any existing pending save for the same field
    this.queue = this.queue.filter(item => 
      !(item.taskId === taskId && item.field === field)
    );

    // Add the new save operation
    const saveOperation = {
      id: `${taskId}-${field}-${Date.now()}`,
      taskId,
      field,
      value,
      saveFunction,
      timestamp: Date.now(),
      status: 'pending'
    };

    this.queue.push(saveOperation);
    this.notifyListeners();
    
    // Start processing if not already running
    if (!this.processing) {
      this.processQueue();
    }

    return saveOperation.id;
  }

  // Process the queue
  async processQueue() {
    if (this.processing || this.queue.length === 0) {
      return;
    }

    this.processing = true;

    while (this.queue.length > 0) {
      const operation = this.queue[0];
      
      try {
        operation.status = 'processing';
        this.notifyListeners();
        
        // Execute the save
        const result = await operation.saveFunction(operation.field, operation.value);
        
        // Remove from queue on success
        this.queue.shift();
        operation.status = 'completed';
        this.retryMap.delete(operation.id);
      } catch (error) {
        console.error(`[SaveQueue] Error saving ${operation.field}:`, error);
        
        // Track retry attempts
        const retries = this.retryMap.get(operation.id) || 0;
        
        if (retries < this.maxRetries) {
          // Move to the end of queue for retry
          this.queue.shift();
          operation.status = 'retrying';
          operation.retryCount = retries + 1;
          this.queue.push(operation);
          this.retryMap.set(operation.id, retries + 1);
          
          // Wait before retrying
          await new Promise(resolve => setTimeout(resolve, 1000 * (retries + 1)));
        } else {
          // Max retries reached, remove from queue
          this.queue.shift();
          operation.status = 'failed';
          operation.error = error;
          this.retryMap.delete(operation.id);
          
        }
      }
      
      this.notifyListeners();
    }

    this.processing = false;
  }

  // Get pending operations for a specific task
  getPendingForTask(taskId) {
    return this.queue.filter(item => item.taskId === taskId);
  }

  // Check if there are pending saves for a task
  hasPendingForTask(taskId) {
    return this.queue.some(item => item.taskId === taskId);
  }

  // Clear all pending operations for a task
  clearForTask(taskId) {
    this.queue = this.queue.filter(item => item.taskId !== taskId);
    this.notifyListeners();
  }

  // Add a listener for queue changes
  addListener(callback) {
    this.listeners.add(callback);
  }

  // Remove a listener
  removeListener(callback) {
    this.listeners.delete(callback);
  }

  // Notify all listeners of queue changes
  notifyListeners() {
    this.listeners.forEach(callback => {
      try {
        callback({
          queue: [...this.queue],
          processing: this.processing
        });
      } catch (error) {
        console.error('[SaveQueue] Error in listener:', error);
      }
    });
  }

  // Get queue status
  getStatus() {
    return {
      queueLength: this.queue.length,
      processing: this.processing,
      queue: [...this.queue]
    };
  }
}

// Create a singleton instance
const saveQueueService = new SaveQueueService();

// Export both the service and the class (for testing)
export { saveQueueService, SaveQueueService };
export default saveQueueService;